/**
 * AutoplayPanel.ts — a slide-up sheet to configure & launch autoplay.
 *
 * Scope: number of colors (rounds) and which stakes to use (template). All stop
 * conditions: after N colors / N pictures, single win >= X, tail >= 50x,
 * balance below / above, loss limit. Speed (zen / brisk / turbo) affects ONLY
 * animation pacing, never the math.
 */
import { Container, Graphics } from 'pixi.js';
import { Button, glassPanel, makeText } from './widgets';
import theme, { hex } from '../render/theme';
import { stakeLabel } from '../util/format';
import { Tweens } from '../util/tween';
import { easeOutCubic, easeOutQuart } from '../util/ease';
import type { Viewport } from '../app/AppContext';

export type AutoplaySpeed = 'zen' | 'brisk' | 'turbo';

export interface AutoplayConfig {
  rounds: number; // colors to auto-buy across the picture(s)
  stake: number;
  speed: AutoplaySpeed;
  stop: {
    afterColors?: number;
    afterPictures?: number;
    onSingleWinExceeds?: number;
    stopOnTailX?: number;
    onBalanceBelow?: number;
    onBalanceAbove?: number;
    onLossExceeds?: number;
    onPictureBonus?: boolean;
  };
}

interface Toggle {
  key: string;
  label: string;
  enabled: boolean;
  value?: number;
  values?: number[];
  valueIdx?: number;
  unit?: 'money' | 'x' | 'count';
}

export class AutoplayPanel extends Container {
  private backdrop = new Graphics();
  private sheet = new Container();
  private sheetBg: Graphics;
  private content = new Container();
  private startBtn: Button;
  private closeBtn: Button;

  private speed: AutoplaySpeed = 'brisk';
  private rounds = 8;
  private roundsOptions = [4, 8, 16, 32];
  private roundsIdx = 1;
  private stake: number;
  private betLevels: number[];
  private stakeIdx: number;

  private toggles: Toggle[] = [];
  private toggleNodes: Container[] = [];
  private speedChips: { c: Container; bg: Graphics; key: AutoplaySpeed }[] = [];
  private roundsLabel;
  private stakeChipLabel;

  private w = 0;
  private sheetH = 0;
  private viewportH = 0;
  private onStart: (cfg: AutoplayConfig) => void;

  constructor(
    betLevels: number[],
    currentStake: number,
    colorsRemaining: number,
    onStart: (cfg: AutoplayConfig) => void,
  ) {
    super();
    this.betLevels = betLevels;
    this.stakeIdx = Math.max(0, betLevels.indexOf(currentStake));
    if (this.stakeIdx < 0) this.stakeIdx = 3;
    this.stake = betLevels[this.stakeIdx];
    this.onStart = onStart;
    this.rounds = Math.max(1, colorsRemaining || 8);
    // snap rounds option to >= remaining if small
    this.roundsOptions = Array.from(
      new Set([colorsRemaining || 8, 4, 8, 16, 32].filter((n) => n > 0)),
    ).sort((a, b) => a - b);
    this.roundsIdx = 0;
    this.rounds = this.roundsOptions[0];

    this.eventMode = 'static';
    this.backdrop.eventMode = 'static';
    this.backdrop.on('pointertap', () => this.hide());
    this.addChild(this.backdrop);

    this.sheetBg = glassPanel(300, 480, 26);
    this.sheet.addChild(this.sheetBg);
    this.sheet.addChild(this.content);
    this.addChild(this.sheet);

    const title = makeText('Autoplay', { size: 24, weight: '800', color: theme.color.ink });
    title.position.set(20, 18);
    this.content.addChild(title);

    this.closeBtn = new Button({
      width: 40, height: 40, label: '✕', fill: theme.color.bgLight3,
      textColor: theme.color.muted, fontSize: 18, radius: 20,
      onTap: () => this.hide(),
    });
    this.content.addChild(this.closeBtn);

    this.roundsLabel = makeText('', { size: 16, weight: '800', color: theme.color.ink });
    this.stakeChipLabel = makeText('', { size: 16, weight: '800', color: theme.color.ink });

    this.buildToggles(colorsRemaining);
    this.buildScopeRow();
    this.buildSpeedRow();

    this.startBtn = new Button({
      width: 300, height: 56, label: 'Start autoplay', icon: '∞',
      fill: theme.color.purple, textColor: theme.color.white, fontSize: 19,
      onTap: () => this.start(),
    });
    this.content.addChild(this.startBtn);

    this.alpha = 0;
    this.visible = false;
  }

  private buildScopeRow(): void {
    // rounds stepper
    const minus = new Button({
      width: 40, height: 40, label: '–', fill: theme.color.bgLight3, textColor: theme.color.ink,
      fontSize: 24, radius: 14, onTap: () => this.changeRounds(-1),
    });
    const plus = new Button({
      width: 40, height: 40, label: '+', fill: theme.color.bgLight3, textColor: theme.color.ink,
      fontSize: 22, radius: 14, onTap: () => this.changeRounds(1),
    });
    minus.label = 'rounds-';
    plus.label = 'rounds+';
    this.roundsLabel.text = String(this.rounds);
    this.roundsLabel.anchor.set(0.5);
    this.content.addChild(minus, plus, this.roundsLabel);

    const sMinus = new Button({
      width: 40, height: 40, label: '–', fill: theme.color.bgLight3, textColor: theme.color.ink,
      fontSize: 24, radius: 14, onTap: () => this.changeStake(-1),
    });
    const sPlus = new Button({
      width: 40, height: 40, label: '+', fill: theme.color.bgLight3, textColor: theme.color.ink,
      fontSize: 22, radius: 14, onTap: () => this.changeStake(1),
    });
    sMinus.label = 'stake-';
    sPlus.label = 'stake+';
    this.stakeChipLabel.text = stakeLabel(this.stake);
    this.stakeChipLabel.anchor.set(0.5);
    this.content.addChild(sMinus, sPlus, this.stakeChipLabel);
  }

  private buildSpeedRow(): void {
    const speeds: { k: AutoplaySpeed; label: string }[] = [
      { k: 'zen', label: 'Zen' },
      { k: 'brisk', label: 'Brisk' },
      { k: 'turbo', label: 'Turbo' },
    ];
    for (const s of speeds) {
      const c = new Container();
      const bg = new Graphics();
      const t = makeText(s.label, { size: 14, weight: '800', color: theme.color.ink });
      t.anchor.set(0.5);
      c.addChild(bg, t);
      c.eventMode = 'static';
      c.cursor = 'pointer';
      c.on('pointertap', () => {
        this.speed = s.k;
        this.redrawSpeed();
      });
      (c as Container & { _label?: typeof t })._label = t;
      this.speedChips.push({ c, bg, key: s.k });
      this.content.addChild(c);
    }
    this.redrawSpeed();
  }

  private redrawSpeed(): void {
    for (const sc of this.speedChips) {
      const active = sc.key === this.speed;
      const lbl = (sc.c as Container & { _label?: { style: { fill: number } } })._label;
      sc.bg.clear();
      sc.bg.roundRect(0, 0, 88, 40, 14).fill(active ? hex(theme.palette.purple) : theme.color.bgLight3);
      if (lbl) lbl.style.fill = active ? theme.color.white : theme.color.ink;
    }
  }

  private buildToggles(colorsRemaining: number): void {
    this.toggles = [
      { key: 'afterColors', label: 'Stop after N colors', enabled: false, values: [4, 8, 16, 32], valueIdx: 1, unit: 'count' },
      { key: 'afterPictures', label: 'Stop after N pictures', enabled: false, values: [1, 2, 3, 5], valueIdx: 0, unit: 'count' },
      { key: 'onSingleWinExceeds', label: 'Single win ≥', enabled: true, values: [5, 10, 25, 50, 100, 250], valueIdx: 1, unit: 'money' },
      { key: 'stopOnTailX', label: 'Tail multiple ≥', enabled: false, values: [20, 50, 100, 250, 500], valueIdx: 1, unit: 'x' },
      { key: 'onBalanceBelow', label: 'Balance falls below', enabled: false, values: [50, 100, 250, 500], valueIdx: 1, unit: 'money' },
      { key: 'onBalanceAbove', label: 'Balance rises above', enabled: false, values: [1500, 2000, 5000], valueIdx: 0, unit: 'money' },
      { key: 'onLossExceeds', label: 'Loss limit', enabled: true, values: [25, 50, 100, 250, 500], valueIdx: 2, unit: 'money' },
      { key: 'onPictureBonus', label: 'Stop on any picture bonus', enabled: false, unit: 'count' },
    ];
    void colorsRemaining;
    for (const tg of this.toggles) {
      const node = this.buildToggleNode(tg);
      this.toggleNodes.push(node);
      this.content.addChild(node);
    }
  }

  private buildToggleNode(tg: Toggle): Container {
    const node = new Container();
    const bg = new Graphics();
    const label = makeText(tg.label, { size: 14, weight: '700', color: theme.color.ink });
    label.position.set(14, 14);
    const check = new Graphics();

    // value chip (if it has values)
    const valLabel = makeText('', { size: 14, weight: '800', color: theme.color.purpleDeep });
    valLabel.anchor.set(1, 0.5);
    const valDec = new Container();
    const valInc = new Container();

    node.addChild(bg, label, check, valLabel);

    const redraw = () => {
      bg.clear();
      bg.roundRect(0, 0, this.w - 40, 46, 14).fill({
        color: tg.enabled ? hex(theme.palette.purple) : theme.color.bgLight3,
        alpha: tg.enabled ? 0.14 : 0.7,
      });
      // checkbox
      check.clear();
      check.roundRect(this.w - 40 - 36, 11, 24, 24, 7).fill(
        tg.enabled ? hex(theme.palette.purple) : theme.color.white,
      );
      check.roundRect(this.w - 40 - 36, 11, 24, 24, 7).stroke({
        width: 1.5, color: tg.enabled ? hex(theme.palette.purple) : theme.color.hint, alpha: 0.6,
      });
      if (tg.enabled) {
        check.moveTo(this.w - 40 - 30, 23).lineTo(this.w - 40 - 26, 28).lineTo(this.w - 40 - 18, 17)
          .stroke({ width: 2.5, color: theme.color.white, cap: 'round', join: 'round' });
      }
      // value
      if (tg.values) {
        valLabel.text = this.formatValue(tg);
        valLabel.position.set(this.w - 40 - 48, 23);
        valLabel.visible = tg.enabled;
        valDec.visible = tg.enabled;
        valInc.visible = tg.enabled;
      } else {
        valLabel.visible = false;
      }
    };

    // value steppers
    if (tg.values) {
      const dec = makeText('‹', { size: 22, weight: '800', color: theme.color.purpleDeep });
      const inc = makeText('›', { size: 22, weight: '800', color: theme.color.purpleDeep });
      dec.anchor.set(0.5);
      inc.anchor.set(0.5);
      valDec.addChild(dec);
      valInc.addChild(inc);
      valDec.eventMode = 'static';
      valInc.eventMode = 'static';
      valDec.cursor = 'pointer';
      valInc.cursor = 'pointer';
      node.addChild(valDec, valInc);
      valDec.on('pointertap', (e) => {
        e.stopPropagation();
        if (!tg.enabled) return;
        tg.valueIdx = Math.max(0, (tg.valueIdx ?? 0) - 1);
        redraw();
      });
      valInc.on('pointertap', (e) => {
        e.stopPropagation();
        if (!tg.enabled) return;
        tg.valueIdx = Math.min((tg.values!.length - 1), (tg.valueIdx ?? 0) + 1);
        redraw();
      });
      // positions set in layout
      (node as Container & { _valDec?: Container; _valInc?: Container; _redraw?: () => void })._valDec = valDec;
      (node as Container & { _valInc?: Container })._valInc = valInc;
    }
    (node as Container & { _redraw?: () => void; _tg?: Toggle })._redraw = redraw;
    (node as Container & { _tg?: Toggle })._tg = tg;

    // tap to toggle (on the row area, not the value steppers)
    const hit = new Graphics();
    hit.rect(0, 0, this.w - 40 - 110, 46).fill({ color: 0, alpha: 0.001 });
    hit.eventMode = 'static';
    hit.cursor = 'pointer';
    hit.on('pointertap', () => {
      tg.enabled = !tg.enabled;
      redraw();
    });
    node.addChildAt(hit, 1);
    redraw();
    return node;
  }

  private formatValue(tg: Toggle): string {
    if (!tg.values || tg.valueIdx == null) return '';
    const v = tg.values[tg.valueIdx];
    if (tg.unit === 'money') return stakeLabel(v);
    if (tg.unit === 'x') return 'x' + v;
    return String(v);
  }

  private changeRounds(dir: number): void {
    this.roundsIdx = Math.max(0, Math.min(this.roundsOptions.length - 1, this.roundsIdx + dir));
    this.rounds = this.roundsOptions[this.roundsIdx];
    this.roundsLabel.text = String(this.rounds);
  }

  private changeStake(dir: number): void {
    this.stakeIdx = Math.max(0, Math.min(this.betLevels.length - 1, this.stakeIdx + dir));
    this.stake = this.betLevels[this.stakeIdx];
    this.stakeChipLabel.text = stakeLabel(this.stake);
  }

  private start(): void {
    const stop: AutoplayConfig['stop'] = {};
    for (const tg of this.toggles) {
      if (!tg.enabled) continue;
      if (tg.key === 'onPictureBonus') {
        stop.onPictureBonus = true;
      } else if (tg.values && tg.valueIdx != null) {
        const v = tg.values[tg.valueIdx];
        (stop as Record<string, number>)[tg.key] = v;
      }
    }
    this.onStart({ rounds: this.rounds, stake: this.stake, speed: this.speed, stop });
    this.hide();
  }

  show(v: Viewport): void {
    this.visible = true;
    this.layout(v);
    this.sheet.position.y = v.height;
    Tweens.to({
      from: 0, to: 1, duration: theme.motion.slow, ease: easeOutCubic,
      onUpdate: (t) => {
        this.alpha = t;
        this.sheet.position.y = v.height - this.sheetH * t;
      },
    });
  }

  hide(): void {
    if (!this.visible) return;
    Tweens.to({
      from: 1, to: 0, duration: theme.motion.base, ease: easeOutQuart,
      onUpdate: (t) => {
        this.alpha = t;
        this.sheet.position.y = this.viewportH - this.sheetH * t;
      },
      onComplete: () => {
        this.visible = false;
      },
    });
  }

  layout(v: Viewport): void {
    this.viewportH = v.height;
    this.w = Math.min(440, v.width);
    const sx = (v.width - this.w) / 2;

    this.backdrop.clear();
    this.backdrop.rect(0, 0, v.width, v.height).fill({ color: theme.color.bgDark2, alpha: 0.42 });

    // measure sheet height
    const innerW = this.w;
    const togglesH = this.toggles.length * 54;
    this.sheetH = 360 + togglesH;
    if (this.sheetH > v.height - 40) this.sheetH = v.height - 40;

    this.sheet.position.set(sx, v.height - this.sheetH);
    this.sheetBg.clear();
    this.sheetBg.roundRect(0, 0, innerW, this.sheetH + 40, 26).fill({ color: theme.color.white, alpha: 0.96 });
    this.sheetBg.roundRect(0, 0, innerW, this.sheetH + 40, 26).stroke({ width: 1, color: theme.color.white, alpha: 0.9 });
    // grabber
    this.sheetBg.roundRect(innerW / 2 - 22, 8, 44, 5, 3).fill({ color: theme.color.hint, alpha: 0.5 });

    this.closeBtn.position.set(innerW - 52, 14);

    // scope row: rounds + stake
    let y = 64;
    const rowLabelColors = makeChildLabel(this.content, 'scope-colors', 'Colors', 20, y);
    void rowLabelColors;
    const minus = this.content.getChildByLabel?.('rounds-') as Button | null;
    const plus = this.content.getChildByLabel?.('rounds+') as Button | null;
    const sMinus = this.content.getChildByLabel?.('stake-') as Button | null;
    const sPlus = this.content.getChildByLabel?.('stake+') as Button | null;
    y += 24;
    if (minus) minus.position.set(20, y);
    this.roundsLabel.position.set(20 + 40 + 36, y + 20);
    if (plus) plus.position.set(20 + 40 + 72, y);

    const stakeX = innerW / 2 + 10;
    if (sMinus) sMinus.position.set(stakeX, y);
    this.stakeChipLabel.position.set(stakeX + 40 + 44, y + 20);
    if (sPlus) sPlus.position.set(stakeX + 40 + 88, y);
    makeChildLabel(this.content, 'scope-stake', 'Stake', stakeX, y - 24);

    // speed row
    y += 60;
    makeChildLabel(this.content, 'speed-label', 'Speed (visual only)', 20, y);
    y += 24;
    this.speedChips.forEach((sc, i) => {
      sc.c.position.set(20 + i * 96, y);
    });

    // toggles
    y += 56;
    makeChildLabel(this.content, 'stop-label', 'Stop conditions', 20, y);
    y += 24;
    this.toggleNodes.forEach((node, i) => {
      node.position.set(20, y + i * 54);
      const n = node as Container & { _redraw?: () => void; _valDec?: Container; _valInc?: Container };
      n._redraw?.();
      if (n._valDec) n._valDec.position.set(this.w - 40 - 96, 23);
      if (n._valInc) n._valInc.position.set(this.w - 40 - 52, 23);
    });
    y += this.toggleNodes.length * 54 + 8;

    // start button
    this.startBtn.resize(innerW - 40, 56);
    this.startBtn.position.set(20, Math.min(y, this.sheetH - 18));
  }
}

/** Create-or-update a named label child on a container. */
function makeChildLabel(parent: Container, name: string, text: string, x: number, y: number) {
  let t = parent.getChildByLabel?.(name) as ReturnType<typeof makeText> | null;
  if (!t) {
    t = makeText(text, { size: 13, weight: '800', color: theme.color.muted });
    t.label = name;
    parent.addChild(t);
  }
  t.text = text;
  t.position.set(x, y);
  return t;
}
